/**
 * Parse a date string (YYYY-MM-DD) as LOCAL time, not UTC.
 *
 * JavaScript's `new Date('2024-01-15')` treats the string as UTC midnight,
 * which causes the date to display as the previous day for users in UTC-offset
 * timezones. This function avoids that by constructing the Date in local time.
 *
 * @param {string|Date|null|undefined} dateInput - A YYYY-MM-DD string or Date object
 * @returns {Date|null} A Date object in local time, or null if input is falsy
 */
export function parseLocalDate(dateInput) {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput;

  const str = String(dateInput);

  // If it's a full ISO timestamp (contains 'T' or 'Z'), parse normally — those are already timezone-aware
  if (str?.includes('T') || str?.includes('Z')) {
    return new Date(str);
  }

  // Parse YYYY-MM-DD as local time
  const parts = str?.split('-');
  if (parts?.length === 3) {
    const year = parseInt(parts?.[0], 10);
    const month = parseInt(parts?.[1], 10) - 1; // months are 0-indexed
    const day = parseInt(parts?.[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      return new Date(year, month, day);
    }
  }

  // Fallback for any other format
  return new Date(str);
}

/**
 * Convert any date value to a clean YYYY-MM-DD string for use in
 * <input type="date"> value props.
 *
 * Handles ISO timestamps (e.g. "2024-01-15T00:00:00+00:00") by extracting
 * just the date portion, preventing the off-by-one day issue caused by
 * UTC-to-local timezone conversion.
 *
 * @param {string|Date|null|undefined} dateInput
 * @returns {string} YYYY-MM-DD string, or '' if input is falsy
 */
export function toDateInputValue(dateInput) {
  if (!dateInput) return '';
  const str = String(dateInput);
  // If it already looks like YYYY-MM-DD (10 chars, no T/Z), return as-is
  if (str?.length === 10 && !str?.includes('T') && !str?.includes('Z')) {
    return str;
  }
  // For ISO timestamps, extract just the date part before 'T'
  if (str?.includes('T')) {
    return str?.split('T')?.[0];
  }
  // For any other format, try to build YYYY-MM-DD via local date parsing
  const d = parseLocalDate(str);
  if (!d || isNaN(d?.getTime())) return '';
  const year = d?.getFullYear();
  const month = String(d?.getMonth() + 1)?.padStart(2, '0');
  const day = String(d?.getDate())?.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a date value for display (e.g. "Jan 15, 2024" or "01/15/2024").
 * Uses parseLocalDate internally to avoid UTC off-by-one day errors.
 *
 * @param {string|Date|null|undefined} dateInput
 * @param {Intl.DateTimeFormatOptions} [options] - toLocaleDateString options
 * @param {string} [locale] - locale string, defaults to 'en-US'
 * @returns {string} formatted date string, or fallback if input is falsy/invalid
 */
export function formatDisplayDate(dateInput, options, locale = 'en-US') {
  if (!dateInput) return '—';
  const d = parseLocalDate(dateInput);
  if (!d || isNaN(d?.getTime())) return '—';
  if (options) {
    return d?.toLocaleDateString(locale, options);
  }
  // Default: MM/DD/YYYY
  const month = String(d?.getMonth() + 1)?.padStart(2, '0');
  const day = String(d?.getDate())?.padStart(2, '0');
  const year = d?.getFullYear();
  return `${month}/${day}/${year}`;
}
